<template>
  <v-container fluid class="pa-4 detalles-container">
    <v-row class="h-100">
      <v-col cols="12" lg="3" class="d-flex">
        <DatosAlumnoCard
          class="flex-grow-1 datos-alumno-card"
          :alumno="alumno"
          :editable="editable"
          :editableAlumno="editableAlumno"
          :fotoAlumno="fotoAlumno"
          :edad="edad"
          :generos="generos"
          :tieneFoto="tieneFotoAlumno"
          :eliminandoFoto="eliminandoFoto"
          @toggle-edit="toggleEdit"
          @update="handleAlumnoUpdate"
          @delete-photo="eliminarFotoAlumno"
        />
      </v-col>

      <v-col cols="12" lg="9">
        <CursosMatriculadosCard
          :matriculas="matriculas"
          :headers="headersCursos"
          :alumnoId="alumno.id"
          @open-dialog="dialogMatricular = true"
          @matricula-eliminada="onMatriculaEliminada"
          @matricula-editada="onMatriculaEditada"
          @pago-registrado="cargarDatos"
        />

        <AlquileresCard
          :alquileres="alquileres"
          :headers="headersAlquileres"
        />
      </v-col>
    </v-row>

    <MatricularCursoDialog
      :dialog="dialogMatricular"
      :alumnoId="alumno.id"
      :es-primera-matricula="matriculas.length === 0"
      @update:dialog="dialogMatricular = $event"
      @curso-matriculado="onCursoMatriculado"
    />
  </v-container>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import DatosAlumnoCard from '@/components/alumno/DatosAlumnoCard.vue';
import CursosMatriculadosCard from '@/components/alumno/CursosMatriculadosCard.vue';
import AlquileresCard from '@/components/alumno/AlquileresCard.vue';
import MatricularCursoDialog from '@/components/alumno/MatricularCursoDialog.vue';

export default {
  components: {
    DatosAlumnoCard,
    CursosMatriculadosCard,
    AlquileresCard,
    MatricularCursoDialog,
  },

  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      alumno: {},
      editableAlumno: {},
      matriculas: [],
      alquileres: [],
      dialogMatricular: false,
      editable: false,
      eliminandoFoto: false,
      fotoVersion: Date.now(),
      fotoRecienActualizada: false,
      fotoBackendUrl: null,
      rutaImagenes: '/images/alumnos/',
      headersCursos: [
        { title: 'Curso', value: 'curso.descripcion' },
        { title: 'Fecha', value: 'fecha' },
        { title: 'Cantidad', value: 'cantidad' },
        { title: 'Costo', value: 'costo' },
        { title: 'Pagado / Total', value: 'pagoResumen', align: 'center' },
        { title: 'Debe', value: 'deuda', align: 'center' },
        { title: 'Pago POS', value: 'visa', align: 'center' },
      ],
      headersAlquileres: [
        { title: 'Marca', value: 'auto.marca' },
        { title: 'Modelo', value: 'auto.modelo' },
        { title: 'Placa', value: 'auto.placa' },
      ],
    };
  },

  computed: {
    ...mapGetters('alumno', ['generos']),
    nombreCompleto() {
      const { nombres, apellido_paterno, apellido_materno } = this.alumno;
      return `${nombres || ''} ${apellido_paterno || ''} ${apellido_materno || ''}`.trim();
    },
    edad() {
      if (!this.editableAlumno.fecha_nacimiento) return '';
      return moment(this.editableAlumno.fecha_nacimiento).diff(moment(), 'years') * -1 + ' AÑOS';
    },
    tieneFotoAlumno() {
      return Boolean(
        this.alumno?.foto_url ||
        this.alumno?.ruta_foto ||
        this.editableAlumno?.fotoPreview ||
        this.fotoRecienActualizada
      );
    },
    fotoAlumno() {
      if (this.fotoBackendUrl) {
        return this.fotoBackendUrl;
      }

      if (this.alumno.foto_url) {
        return this.alumno.foto_url;
      }

      return this.alumno.ruta_foto
        ? this.rutaImagenes + this.alumno.ruta_foto
        : require('@/assets/user-icon.jpg');
    },
  },

  methods: {
    ...mapActions('alumno', ['fetchAlumnoById', 'saveAlumno', 'fetchGeneros', 'deleteAlumnoFoto']),

    async cargarFotoAlumno() {
      if (!this.alumno?.id || !this.tieneFotoAlumno) {
        this.fotoBackendUrl = null;
        return;
      }

      try {
        const { data } = await axios.get(`/api/alumno/${this.alumno.id}/foto`, {
          params: { v: this.fotoVersion },
        });
        this.fotoBackendUrl = data?.url || null;
      } catch (error) {
        console.error('Error al obtener la foto del alumno:', error);
        this.fotoBackendUrl = null;
      }
    },

    async cargarDatos() {
      try {
        const data = await this.fetchAlumnoById(this.id);

        this.alumno = data;
        this.fotoRecienActualizada = Boolean(data?.foto_url || data?.ruta_foto || this.fotoRecienActualizada);
        this.editableAlumno = {
          ...JSON.parse(JSON.stringify(data)),
          genero_id: data.genero_id ?? data.genero?.id ?? '',
          tipo_documento_id: data.tipo_documento_id ?? 1,
          foto: null,
          fotoPreview: null,
        };
        this.matriculas = data.matriculas || [];
        this.alquileres = data.alquileres || [];
        await this.cargarFotoAlumno();

      } catch (error) {
        console.error('Error al cargar datos:', error);
      }
    },

    handleAlumnoUpdate(updatedAlumno) {
      this.editableAlumno = { ...updatedAlumno };
    },

    async eliminarFotoAlumno() {
      if (!this.alumno?.id || !this.tieneFotoAlumno) return;

      this.eliminandoFoto = true;
      try {
        await this.deleteAlumnoFoto(this.alumno.id);
        this.fotoBackendUrl = null;
        this.fotoRecienActualizada = false;
        this.fotoVersion = Date.now();
        await this.cargarDatos();
      } catch (error) {
        console.error('Error al eliminar la foto del alumno:', error);
      } finally {
        this.eliminandoFoto = false;
      }
    },

    async toggleEdit() {
      if (this.editable) {
        try {
          const seActualizoFoto = this.editableAlumno.foto instanceof File;
          const dataToSave = {
            id: this.alumno.id,
            nombres: this.editableAlumno.nombres,
            apellido_paterno: this.editableAlumno.apellido_paterno,
            apellido_materno: this.editableAlumno.apellido_materno,
            tipo_documento_id: this.editableAlumno.tipo_documento_id || 1,
            numero_documento: this.editableAlumno.numero_documento,
            fecha_nacimiento: this.editableAlumno.fecha_nacimiento || null,
            celular: this.editableAlumno.celular,
            email: this.editableAlumno.email || null,
            direccion: this.editableAlumno.direccion || null,
            ruta_foto: this.editableAlumno.ruta_foto || null,
            genero_id: this.editableAlumno.genero_id,
            foto: this.editableAlumno.foto,
          };

          await this.saveAlumno(dataToSave);
          if (seActualizoFoto) {
            this.fotoRecienActualizada = true;
            this.fotoVersion = Date.now();
          }
          await this.cargarDatos();
        } catch (e) {
          console.error('Error al guardar alumno:', e);
          this.editableAlumno = {
            ...JSON.parse(JSON.stringify(this.alumno)),
            genero_id: this.alumno.genero_id ?? this.alumno.genero?.id ?? '',
            tipo_documento_id: this.alumno.tipo_documento_id ?? 1,
            foto: null,
            fotoPreview: null,
          };
          return;
        }
      }
      this.editable = !this.editable;
    },

    onCursoMatriculado() {
      this.cargarDatos();
    },

    onMatriculaEliminada() {
      this.cargarDatos();
    },

    onMatriculaEditada() {
      this.cargarDatos();
    },
  },

  async created() {
    await this.fetchGeneros();
    this.cargarDatos();
  },
};
</script>

<style scoped>
.detalles-container {
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
}

.detalles-container > .v-row {
  flex: 1 1 auto;
  min-height: 0;
}

.datos-alumno-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.datos-alumno-card :deep(.v-card-text) {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}
</style>
